import { z } from 'zod';

/**
 * "Voor jou": wat er voor deze medewerker open staat, uit alle modules samen (UI-7/UI-8, §10.1).
 *
 * Dit contract staat in `common` en niet bij een module, omdat geen module er eigenaar van is. Een
 * bericht dat nog bevestigd moet worden komt uit `reach`, een formulier uit `forms`, een checklist
 * uit `tasks` en een agenda-item uit `agenda` — vier modules die elkaar niet mogen kennen
 * (ADR-0016) en die alle vier uit kunnen staan (ADR-0021). Ze melden zich aan bij een poort in de
 * kernel; de kaart kent alleen die poort.
 *
 * Wat hier **niet** in staat: een module-eigen veld. Zodra dit contract een `formId` of een
 * `runId` apart draagt, weet de kaart weer welke modules er bestaan — en dan is de poort
 * versiering. Elke regel draagt een `href`, en dat is genoeg: ze leidt naar het scherm waar je
 * hem afhandelt.
 */

/** Welke module deze regel aanleverde. Voor het icoon en voor het sorteren, niet voor logica. */
export const openItemSourceSchema = z.enum(['reach', 'forms', 'tasks', 'agenda']);
export type OpenItemSource = z.infer<typeof openItemSourceSchema>;

export const openItemSchema = z.object({
  /**
   * Uniek binnen het antwoord, en stabiel tussen twee aanroepen: `<bron>:<id>`.
   *
   * Stabiel omdat de lijst in de app een sleutel nodig heeft die niet verschuift. Met de index als
   * sleutel zou het afhandelen van de eerste regel de rest laten herrenderen — en op een telefoon
   * is dat een tik die op het verkeerde item landt.
   */
  key: z.string(),
  source: openItemSourceSchema,
  title: z.string(),
  /** De regel eronder: "Deadline 30/09 · nog 13 dagen", "Anoniem · 5 min". */
  meta: z.string(),
  /** Waar je hem afhandelt. Altijd een pad binnen de app, nooit een externe URL. */
  href: z.string(),
  /**
   * Wanneer het af moet, als dat bestaat. `null` betekent "geen termijn", niet "geen haast".
   *
   * De urgentie wordt hiervan **afgeleid** en niet apart gezet: een module die zelf `urgent: true`
   * mag zeggen, maakt urgentie een smaak in plaats van een datum.
   */
  dueAt: z.string().datetime().nullable(),
  /** Afgeleid uit `dueAt`: binnen twee weken, of al verstreken. */
  urgent: z.boolean(),
  /**
   * De tekst onder de titel in de detailkaart van "Mijn taken" (SCH-5).
   *
   * Kort, en uit de module zelf: de aanhef van het bericht, de omschrijving van het formulier.
   * `null` betekent dat er niets te zeggen valt — dan staat er niets, geen plaatshouder.
   *
   * De **kaart** op het beginscherm gebruikt dit veld niet: daar is `meta` de regel eronder, en
   * twee regels tekst per rij past niet in een kolom van 320 px.
   */
  description: z.string().nullable().default(null),
  /**
   * Wie het stuurde: naam, afdeling en tijdstip ("Sarah De Backer · HR · 2 uur geleden").
   *
   * De naam van de **afzender** mag — dezelfde regel als op de berichtkaart. Die van een ontvanger
   * niet (ADR-0014): wie iets nog moet bevestigen, is geen gegeven dat op andermans scherm hoort.
   * Bestaat er geen afzender — een checklist uit een planner, een formulier zonder opsteller — dan
   * blijft de regel weg.
   */
  sender: z
    .object({
      name: z.string(),
      department: z.string().nullable(),
      at: z.string().datetime().nullable(),
    })
    .nullable()
    .default(null),
  /**
   * Wat er moet gebeuren: "Bevestig", "Inschrijven", "Invullen", "Bekijken".
   *
   * Komt van de module, want alleen die weet wat de handeling is. Net als `meta` is dit
   * **Nederlandse tekst van de server** — dezelfde schuld, en ze staat als één punt in
   * `docs/product/demo-schuld.md` in plaats van als twee.
   */
  actionLabel: z.string().nullable().default(null),
});
export type OpenItem = z.infer<typeof openItemSchema>;

export const openItemListSchema = z.object({ items: z.array(openItemSchema) });
export type OpenItemList = z.infer<typeof openItemListSchema>;

/**
 * De vraag van het scherm "Mijn taken" tegenover die van de kaart "Voor jou" (SCH-5).
 *
 * Een vlag en geen `limit`: de client hoort niet te kiezen hoeveel er op de kaart passen - dat is
 * een eigenschap van de kaart, en die staat op de server zodat alle drie de schermbreedtes
 * hetzelfde lijstje krijgen. De vlag zegt alleen wat de vraag is: "de kaart" of "de werklijst".
 *
 * Standaard uit, zodat de bestaande route geen gedrag verliest.
 */
export const openItemQuerySchema = z.object({
  all: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((waarde) => waarde === true || waarde === 'true')
    .default(false),
});
export type OpenItemQuery = z.input<typeof openItemQuerySchema>;

/**
 * Het aantal dagen waarbinnen iets dringend is.
 *
 * Veertien en niet zeven: de mockup zet "nog 13 dagen" in het oranje, en dat is ook de termijn die
 * past bij wat hier staat. Een veiligheidsprocedure die over twee weken bevestigd moet zijn, hoort
 * nú op te vallen — wie er pas op de laatste dag aan herinnerd wordt, leest niet meer, hij tikt.
 */
export const URGENT_WITHIN_DAYS = 14;

/**
 * Is dit dringend, gegeven de termijn en nu?
 *
 * Een pure functie met `now` als argument, zodat ze te testen is zonder de klok te verzetten —
 * dezelfde aanpak als `bulletinPeriodLine` en de checklistplanner (ADR-0064).
 *
 * Verstreken is óók dringend, en dat is de reden dat dit geen `dueAt < now + 14d` is: een deadline
 * van vorige week zou met die vergelijking wel dringend zijn, maar een deadline van vorig jaar ook,
 * en beide horen hier hetzelfde te betekenen. Wat weg is, is weg te werken.
 */
export function isUrgent(dueAt: string | null, now: Date): boolean {
  if (!dueAt) return false;
  const verschil = new Date(dueAt).getTime() - now.getTime();
  return verschil < URGENT_WITHIN_DAYS * 86_400_000;
}
